import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons/static";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Animated,
	type LayoutChangeEvent,
	Pressable,
	StyleSheet,
	View,
	useWindowDimensions,
} from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import InlineMarkdownText from "../MarkdownRenderer/InlineMarkdownText";
import { stripInlineMarkdown } from "../MarkdownRenderer/inline-markdown";
import { Skeleton } from "../Skeleton";
import {
	ICON_NAMES,
	INSIGHT_CALLOUT_DIMENSIONS,
	SHIMMER_ANIMATION,
} from "./constants";

export type InsightCalloutProps = {
	text?: string | null;
	isLoading?: boolean;
	variant?: "footer" | "flat";
	animated?: boolean;
	showLeadingIcon?: boolean;
	showTrailingIcon?: boolean;
	onPress?: () => void;
	onActionPress?: () => void;
	actionLabel?: string;
	backgroundColor?: string;
	iconColor?: string;
	leadingIcon?: React.ReactNode;
	expandable?: boolean;
};

const resolveExpandable = (
	expandableProp: boolean | undefined,
	isMobile: boolean,
	showTrailingIcon: boolean,
	onActionPress?: () => void,
) => expandableProp ?? (isMobile && showTrailingIcon && !onActionPress);

const resolvePressHandlers = (
	expandable: boolean,
	toggleExpanded: () => void,
	onPress?: () => void,
	onActionPress?: () => void,
) =>
	expandable
		? { textPress: onPress ?? toggleExpanded, trailingPress: toggleExpanded }
		: { textPress: onPress, trailingPress: onActionPress ?? onPress };

const resolveTrailing = (
	isLoading: boolean,
	showTrailingIcon: boolean,
	canExpand: boolean,
	trailingPress?: () => void,
) => {
	const interactive = !isLoading && showTrailingIcon && Boolean(trailingPress);
	return {
		showChevron: interactive && canExpand,
		showActionLink: interactive && !canExpand,
	};
};

const collapsedLineCount = (expandable: boolean, isExpanded: boolean) =>
	expandable && !isExpanded
		? INSIGHT_CALLOUT_DIMENSIONS.collapsedLines
		: undefined;

const expandedA11yState = (expandable: boolean, isExpanded: boolean) =>
	expandable ? { expanded: isExpanded } : undefined;

type TrailingActionProps = {
	onPress?: () => void;
	right: number;
	iconSize: number;
	color: string;
	expandable: boolean;
	isExpanded: boolean;
};

const TrailingAction = ({
	onPress,
	right,
	iconSize,
	color,
	expandable,
	isExpanded,
}: TrailingActionProps) => {
	const label = isExpanded ? "Collapse insight" : "Expand insight";
	const icon = isExpanded ? ICON_NAMES.collapse : ICON_NAMES.expand;
	return (
		<Pressable
			onPress={onPress}
			testID="insight-callout-trailing-icon"
			style={{
				position: "absolute",
				top: 0,
				bottom: 0,
				right,
				justifyContent: "center",
			}}
			hitSlop={12}
			accessibilityRole="button"
			accessibilityLabel={label}
			accessibilityState={expandedA11yState(expandable, isExpanded)}
		>
			<MaterialCommunityIcons name={icon} size={iconSize} color={color} />
		</Pressable>
	);
};

const ActionLink = ({
	onPress,
	label,
}: {
	onPress?: () => void;
	label?: string;
}) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const resolvedLabel = label ?? t("common.viewDetails");
	return (
		<Pressable
			onPress={onPress}
			testID="insight-callout-action-link"
			hitSlop={12}
			accessibilityRole="button"
			accessibilityLabel={resolvedLabel}
		>
			<Text
				variant="labelLarge"
				style={{ color: theme.colors.primary, fontWeight: "600" }}
			>
				{resolvedLabel}
			</Text>
		</Pressable>
	);
};

export const InsightCallout = ({
	text,
	isLoading = false,
	variant = "footer",
	animated = false,
	showLeadingIcon = true,
	showTrailingIcon = true,
	onPress,
	onActionPress,
	actionLabel,
	backgroundColor,
	iconColor,
	leadingIcon,
	expandable: expandableProp,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: InsightCalloutProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [textOverflows, setTextOverflows] = useState(false);
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const expandable = resolveExpandable(
		expandableProp,
		isMobile,
		showTrailingIcon,
		onActionPress,
	);
	const canExpand = expandable && textOverflows;
	const calloutBackground = backgroundColor ?? theme.colors.secondaryContainer;
	const calloutIconColor = iconColor ?? theme.colors.onSecondaryContainer;
	const { width: screenWidth } = useWindowDimensions();
	const shimmerTranslateX = useRef(new Animated.Value(-screenWidth)).current;

	const { iconSize, horizontalPadding, verticalPadding, gap } =
		INSIGHT_CALLOUT_DIMENSIONS;
	const textLineHeight = theme.fonts.bodySmall.lineHeight ?? iconSize;

	useEffect(() => {
		if (animated) {
			const loop = Animated.loop(
				Animated.timing(shimmerTranslateX, {
					toValue: screenWidth,
					duration: SHIMMER_ANIMATION.duration,
					useNativeDriver: true,
				}),
			);
			loop.start();
			return () => loop.stop();
		}
		shimmerTranslateX.setValue(-screenWidth);
	}, [animated, shimmerTranslateX, screenWidth]);

	const toggleExpanded = () => setIsExpanded((previous) => !previous);
	const { textPress, trailingPress } = resolvePressHandlers(
		canExpand,
		toggleExpanded,
		onPress,
		onActionPress,
	);
	const { showChevron, showActionLink } = resolveTrailing(
		isLoading,
		showTrailingIcon,
		canExpand,
		trailingPress,
	);

	const handleMeasureText = (event: LayoutChangeEvent) => {
		setTextOverflows(
			event.nativeEvent.layout.height >
				textLineHeight * INSIGHT_CALLOUT_DIMENSIONS.collapsedLines + 1,
		);
	};

	const cornerRadius =
		variant === "flat"
			? { borderRadius: theme.roundness * 2 }
			: {
					borderBottomLeftRadius: theme.roundness * 3,
					borderBottomRightRadius: theme.roundness * 3,
				};

	// Icons absolutely positioned so the text block alone sets callout height (no measure, no first-paint jump).
	const contentLeftInset = showLeadingIcon ? iconSize + gap : 0;
	const contentRightInset = showChevron ? iconSize + gap : 0;

	return (
		<View style={{ position: "relative" }} testID="insight-callout">
			<Box
				style={{
					backgroundColor: calloutBackground,
					padding: verticalPadding,
					paddingHorizontal: horizontalPadding,
					...cornerRadius,
				}}
			>
				{isLoading ? (
					<Box gap={6} paddingTop={2} style={{ paddingLeft: contentLeftInset }}>
						<Skeleton height={12} width="90%" />
						<Skeleton height={12} width="70%" />
					</Box>
				) : (
					<Box
						flexDirection="row"
						flexWrap="wrap"
						alignItems="center"
						justifyContent="flex-end"
						columnGap={gap}
						rowGap={4}
						style={{
							paddingLeft: contentLeftInset,
							paddingRight: contentRightInset,
						}}
					>
						<Pressable
							disabled={!textPress}
							onPress={textPress}
							style={{
								flexGrow: 1,
								flexShrink: 1,
								flexBasis: INSIGHT_CALLOUT_DIMENSIONS.textWrapBasis,
								minHeight: iconSize,
								justifyContent: "center",
							}}
							accessibilityRole="button"
							accessibilityLabel={text ? stripInlineMarkdown(text) : undefined}
							accessibilityState={expandedA11yState(canExpand, isExpanded)}
						>
							<InlineMarkdownText
								text={text}
								variant="bodySmall"
								numberOfLines={collapsedLineCount(expandable, isExpanded)}
								style={{
									color: theme.colors.onSurface,
									lineHeight: textLineHeight,
								}}
							/>
							{expandable && (
								// Invisible unclamped copy — measures whether text exceeds the collapsed line count.
								<InlineMarkdownText
									text={text}
									variant="bodySmall"
									accessible={false}
									importantForAccessibility="no-hide-descendants"
									pointerEvents="none"
									onLayout={handleMeasureText}
									style={{
										position: "absolute",
										left: 0,
										right: 0,
										opacity: 0,
										lineHeight: textLineHeight,
									}}
								/>
							)}
						</Pressable>
						{showActionLink && (
							<Box
								alignItems="flex-end"
								style={isMobile ? { flexBasis: "100%" } : undefined}
							>
								<ActionLink onPress={trailingPress} label={actionLabel} />
							</Box>
						)}
					</Box>
				)}

				{showLeadingIcon && (
					<View
						pointerEvents="none"
						testID="insight-callout-leading-icon"
						style={{
							position: "absolute",
							left: horizontalPadding,
							top: verticalPadding,
							width: iconSize,
							height: iconSize,
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						{leadingIcon ?? (
							<MaterialCommunityIcons
								name="lightbulb-on-outline"
								size={iconSize}
								color={calloutIconColor}
							/>
						)}
					</View>
				)}

				{showChevron && (
					<TrailingAction
						onPress={trailingPress}
						right={horizontalPadding}
						iconSize={iconSize}
						color={calloutIconColor}
						expandable={canExpand}
						isExpanded={isExpanded}
					/>
				)}
			</Box>

			{animated && (
				<Animated.View
					style={[
						StyleSheet.absoluteFill,
						{
							transform: [{ translateX: shimmerTranslateX }],
						},
					]}
					pointerEvents="none"
				>
					{/* <LinearGradient
						colors={["transparent", "rgba(255, 255, 255, 0.5)", "transparent"]}
						start={{ x: 0, y: 0 }}
						end={{ x: 1, y: 1 }}
						style={StyleSheet.absoluteFill}
					/> */}
				</Animated.View>
			)}
		</View>
	);
};
